/**
 * I1 · Coach public profile with verified activity stats.
 * I3 · Free trial-lesson booking = signup + booking in one step.
 * I4 · Verified student reviews.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { Button, Card, ErrorText, Field, Input, Spinner, Tag, fmtDate } from '../../lib/ui.tsx';
import { Stars, type CoachCard } from './Directory.tsx';

interface CoachProfileData extends CoachCard {
  reviews: { rating: number; text: string; verified: boolean; createdAt: string; studentName: string }[];
}

export default function CoachPublicProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { me } = useAuth();

  const { data: coach, isLoading } = useQuery({
    queryKey: ['public-coach', id],
    queryFn: () => api<CoachProfileData>(`/api/marketplace/coaches/${id}`),
  });

  if (isLoading) return <Spinner />;
  if (!coach) return <ErrorText error="Coach profile not found" />;

  const stats = coach.stats;

  return (
    <div className="space-y-6">
      {/* I1 · hero */}
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">{coach.name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Stars rating={stats.avgRating} count={stats.ratingCount} />
              {coach.credentialBadge && <Tag tone="green">✓ verified credentials</Tag>}
            </div>
          </div>
        </div>
        {coach.bio && <p className="mt-3 text-sm whitespace-pre-line text-slate-600">{coach.bio}</p>}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {coach.specialties.map((s) => (
            <Tag key={s} tone="brand">
              {s}
            </Tag>
          ))}
          {coach.languages.map((l) => (
            <Tag key={l}>{l}</Tag>
          ))}
        </div>
        {coach.introVideoUrl && (
          <a
            href={coach.introVideoUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-block text-sm font-medium text-brand-700 hover:underline"
          >
            ▶ Watch intro video
          </a>
        )}

        {/* verified activity stats */}
        <div className="mt-4 grid grid-cols-3 gap-2 rounded-lg bg-slate-50 p-3">
          <div className="text-center">
            <p className="text-lg font-bold text-slate-900">
              {stats.medianReviewTurnaroundHours !== null ? `~${stats.medianReviewTurnaroundHours}h` : '—'}
            </p>
            <p className="text-[11px] text-slate-500">median review turnaround</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-slate-900">{stats.activeStudents}</p>
            <p className="text-[11px] text-slate-500">active students</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-slate-900">{stats.reviewsLast90d}</p>
            <p className="text-[11px] text-slate-500">reviews last 90 days</p>
          </div>
        </div>
        <p className="mt-1.5 text-right text-[11px] text-slate-400">activity verified by Solfo</p>
      </Card>

      {/* I3 · trial booking */}
      {me ? (
        <Card title="Book with this coach">
          <p className="text-sm text-slate-600">
            You're already signed in{me.role === 'student' ? ' as a student' : ''} — trial signup is for new
            students.
          </p>
        </Card>
      ) : (
        <TrialSignup coachId={coach.id} />
      )}

      {/* I4 · reviews */}
      <Card title={`Reviews (${coach.reviews.length})`}>
        {coach.reviews.length === 0 ? (
          <p className="text-xs text-slate-400">No reviews yet.</p>
        ) : (
          <ul className="space-y-3">
            {coach.reviews.map((review, i) => (
              <li key={i} className="rounded-lg bg-slate-50 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span aria-hidden className="text-sm text-amber-500">
                    {'★'.repeat(review.rating)}
                    {'☆'.repeat(5 - review.rating)}
                  </span>
                  <span className="text-xs font-medium text-slate-700">{review.studentName}</span>
                  {review.verified && <Tag tone="green">✓ verified student</Tag>}
                  <span className="text-[11px] text-slate-400">{fmtDate(review.createdAt)}</span>
                </div>
                {review.text && <p className="mt-1.5 text-sm text-slate-600">{review.text}</p>}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function TrialSignup({ coachId }: { coachId: string }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', dateOfBirth: '', startsAt: '' });
  const [error, setError] = useState<unknown>(null);

  const set = (key: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const book = useMutation({
    mutationFn: () =>
      api<{ token: string }>('/api/marketplace/trial-signup', {
        body: {
          coachId,
          name: form.name,
          email: form.email,
          password: form.password,
          dateOfBirth: form.dateOfBirth,
          startsAt: new Date(form.startsAt).toISOString(),
        },
      }),
    onSuccess: async (res) => {
      await login(res.token);
      navigate('/app');
    },
    onError: setError,
  });

  const ready = form.name && form.email && form.password.length >= 8 && form.dateOfBirth && form.startsAt;

  return (
    <Card title="Free trial lesson · 30 min">
      <p className="text-sm text-slate-500">
        Book a trial and your student account is created in the same step — you'll land straight in the app.
      </p>
      <form
        className="mt-3 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) book.mutate();
        }}
      >
        <Field label="Your name">
          <Input value={form.name} onChange={set('name')} required />
        </Field>
        <Field label="Email">
          <Input type="email" value={form.email} onChange={set('email')} required />
        </Field>
        <Field label="Password" hint="At least 8 characters">
          <Input type="password" value={form.password} onChange={set('password')} required minLength={8} />
        </Field>
        <Field label="Date of birth" hint="You must be 18 or older">
          <Input type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} required />
        </Field>
        <Field label="Lesson time">
          <Input type="datetime-local" value={form.startsAt} onChange={set('startsAt')} required />
        </Field>
        <Button type="submit" disabled={!ready || book.isPending} className="w-full">
          {book.isPending ? 'Booking…' : 'Book your free trial'}
        </Button>
      </form>
      <ErrorText error={error} />
    </Card>
  );
}
